import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Radar,
  TrendingUp,
  ArrowRight,
  Eye,
  XCircle,
  Mail,
  RefreshCw,
  Filter,
  Activity,
  Globe,
  Building2,
  Zap,
  Clock,
} from "lucide-react";

const TIER_CONFIG = {
  1: { label: "Tier 1", color: "bg-red-500/10 text-red-400 border-red-500/20", dot: "bg-red-500" },
  2: { label: "Tier 2", color: "bg-amber-500/10 text-amber-400 border-amber-500/20", dot: "bg-amber-500" },
  3: { label: "Tier 3", color: "bg-slate-500/10 text-slate-600 border-slate-500/20", dot: "bg-slate-500" },
};

const COUNTRY_FLAGS = {
  "Nigeria": "\uD83C\uDDF3\uD83C\uDDEC",
  "Ghana": "\uD83C\uDDEC\uD83C\uDDED",
  "Kenya": "\uD83C\uDDF0\uD83C\uDDEA",
  "Ivory Coast": "\uD83C\uDDE8\uD83C\uDDEE",
  "Multi": "\uD83C\uDF0D",
};

const STATUS_STYLES = {
  new: "bg-blue-500/10 text-blue-400",
  actioned: "bg-green-500/10 text-green-400",
  dismissed: "bg-slate-500/10 text-slate-500",
  outreach_pending: "bg-purple-500/10 text-purple-400",
  outreach_sent: "bg-emerald-500/10 text-emerald-400",
};

function MetricCard({ icon: Icon, label, value, sub }) {
  return (
    <div data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`} className="bg-white rounded-lg p-4 border border-slate-200">
      <div className="flex items-center gap-2 text-slate-500 text-xs mb-2">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function EventCard({ event, onAction, onDrillDown }) {
  const tier = TIER_CONFIG[event.tier] || TIER_CONFIG[3];
  const flag = COUNTRY_FLAGS[event.country_detected] || "";
  const statusStyle = STATUS_STYLES[event.status] || STATUS_STYLES.new;
  const eventTypeLabel = (event.event_type || "").replace(/_/g, " ");

  return (
    <div
      data-testid={`event-card-${event.event_id}`}
      className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-600/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={tier.color}>
            <span className={`w-1.5 h-1.5 rounded-full ${tier.dot} mr-1.5`} />
            {tier.label}
          </Badge>
          <Badge variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600/50 text-xs">
            {eventTypeLabel}
          </Badge>
          <Badge variant="outline" className={statusStyle}>{event.status}</Badge>
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {event.score && <span className="font-mono font-bold text-slate-600">S:{event.score}</span>}
        </span>
      </div>

      <button
        data-testid={`drilldown-btn-${event.event_id}`}
        className="text-left w-full group/headline"
        onClick={() => onDrillDown(event)}
      >
        <h3 className="text-sm font-medium text-slate-900 mb-2 leading-snug group-hover/headline:text-indigo-600 transition-colors">
          {event.headline}
          <Eye className="w-3.5 h-3.5 inline ml-1.5 opacity-0 group-hover/headline:opacity-100 transition-opacity text-indigo-500" />
        </h3>
      </button>

      <div className="flex items-center gap-3 text-xs text-slate-600 mb-3">
        <span className="flex items-center gap-1">
          <Building2 className="w-3 h-3" /> {event.company_name}
        </span>
        <span className="flex items-center gap-1">
          <Globe className="w-3 h-3" /> {flag} {event.country_detected}
        </span>
        {event.created_at && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(event.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </span>
        )}
      </div>

      {event.commercial_implication && (
        <p className="text-xs text-slate-600 mb-3 bg-slate-50 rounded px-3 py-2 border-l-2 border-amber-500/40">
          {event.commercial_implication}
        </p>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
        <Button
          data-testid={`ask-rex-btn-${event.event_id}`}
          size="sm"
          variant="ghost"
          className="text-xs text-indigo-500 hover:text-indigo-400 hover:bg-indigo-500/10 h-7"
          onClick={() => onDrillDown(event)}
        >
          <Eye className="w-3 h-3 mr-1" /> Ask Rex
        </Button>
        {event.status === "new" && (
          <>
            <Button
              data-testid={`action-btn-${event.event_id}`}
              size="sm"
              variant="ghost"
              className="text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10 h-7"
              onClick={() => onAction(event.event_id, "actioned")}
            >
              <ArrowRight className="w-3 h-3 mr-1" /> Action
            </Button>
            <Button
              data-testid={`draft-outreach-btn-${event.event_id}`}
              size="sm"
              variant="ghost"
              className="text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 h-7"
              onClick={() => onAction(event.event_id, "outreach_drafted")}
            >
              <Mail className="w-3 h-3 mr-1" /> Draft Outreach
            </Button>
            <Button
              data-testid={`dismiss-btn-${event.event_id}`}
              size="sm"
              variant="ghost"
              className="text-xs text-slate-500 hover:text-slate-600 hover:bg-slate-500/10 h-7"
              onClick={() => onAction(event.event_id, "dismissed")}
            >
              <XCircle className="w-3 h-3 mr-1" /> Dismiss
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function OpportunityRadar() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [engineEnabled, setEngineEnabled] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterTier !== "all") params.set("tier", filterTier);
      if (filterCountry !== "all") params.set("country", filterCountry);
      if (filterStatus !== "all") params.set("status", filterStatus);

      const [eventsRes, statusRes] = await Promise.all([
        fetch(`${API}/opportunity/events?${params}`, { credentials: "include" }),
        fetch(`${API}/opportunity/status`, { credentials: "include" }),
      ]);

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }
      if (statusRes.ok) {
        const data = await statusRes.json();
        setEngineEnabled(data.enabled);
        setMetrics(data.metrics);
      }
    } catch (err) {
      console.error("Failed to fetch opportunity data:", err);
    } finally {
      setLoading(false);
    }
  }, [filterTier, filterCountry, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (eventId, actionType) => {
    try {
      const res = await fetch(`${API}/opportunity/events/${eventId}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action_type: actionType }),
      });
      if (res.ok) {
        toast.success(`Event ${actionType.replace("_", " ")}`);
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to record action");
    }
  };

  const handleDrillDown = (event) => {
    const eventTypeLabel = (event.event_type || "").replace(/_/g, " ");
    const prompt = `Tell me more about the ${event.company_name} ${eventTypeLabel} event in ${event.country_detected}. Headline: "${event.headline}". What are the commercial implications for RESILIGENT and what actions should we consider?`;
    navigate("/ask-rex", { state: { prefillPrompt: prompt } });
  };

  if (!engineEnabled && !loading) {
    return (
      <div data-testid="opportunity-radar-disabled" className="flex items-center justify-center h-96">
        <div className="text-center">
          <Radar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-slate-600">Opportunity Engine Disabled</h2>
          <p className="text-sm text-slate-500 mt-1">Enable REX_OPPORTUNITY_ENGINE to activate</p>
        </div>
      </div>
    );
  }

  const tier1Count = events.filter((e) => e.tier === 1).length;
  const tier2Count = events.filter((e) => e.tier === 2).length;
  const tier3Count = events.filter((e) => e.tier === 3).length;

  return (
    <div data-testid="opportunity-radar-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Radar className="w-6 h-6 text-amber-400" />
            Opportunity Radar
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            West Africa & East Africa trigger event intelligence
          </p>
        </div>
        <Button
          data-testid="refresh-radar-btn"
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="border-slate-200 text-slate-300"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Metrics Row */}
      {metrics && (
        <div data-testid="metrics-row" className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard icon={Zap} label="Total Events" value={metrics.total_events} sub={`Last ${metrics.period_days} days`} />
          <MetricCard icon={Activity} label="Follow-up Rate" value={`${metrics.follow_up_rate_percent}%`} sub="Actioned + Outreach" />
          <MetricCard
            icon={TrendingUp}
            label="Tier Breakdown"
            value={`${metrics.tier_breakdown?.tier1 || 0} / ${metrics.tier_breakdown?.tier2 || 0} / ${metrics.tier_breakdown?.tier3 || 0}`}
            sub="T1 / T2 / T3"
          />
          <MetricCard
            icon={Clock}
            label="Avg Response"
            value={metrics.avg_time_to_action_hours ? `${metrics.avg_time_to_action_hours}h` : "--"}
            sub="Time to action"
          />
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="pt-4 pb-3">
          <div data-testid="filters-row" className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <Filter className="w-3.5 h-3.5" /> Filters:
            </div>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger data-testid="filter-tier" className="w-[130px] h-8 text-xs bg-white border-slate-200">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="1">Tier 1 (Hot)</SelectItem>
                <SelectItem value="2">Tier 2 (Warm)</SelectItem>
                <SelectItem value="3">Tier 3 (Watch)</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger data-testid="filter-country" className="w-[150px] h-8 text-xs bg-white border-slate-200">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                <SelectItem value="Nigeria">Nigeria</SelectItem>
                <SelectItem value="Ghana">Ghana</SelectItem>
                <SelectItem value="Kenya">Kenya</SelectItem>
                <SelectItem value="Ivory Coast">Ivory Coast</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status" className="w-[140px] h-8 text-xs bg-white border-slate-200">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="actioned">Actioned</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
                <SelectItem value="outreach_pending">Outreach Pending</SelectItem>
                <SelectItem value="outreach_sent">Outreach Sent</SelectItem>
              </SelectContent>
            </Select>

            <div className="ml-auto flex items-center gap-2 text-xs">
              <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20">{tier1Count} Hot</Badge>
              <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">{tier2Count} Warm</Badge>
              <Badge variant="outline" className="bg-slate-500/10 text-slate-600 border-slate-500/20">{tier3Count} Watch</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">Loading intelligence...</div>
      ) : events.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-16 text-center">
            <Radar className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-600">No trigger events match your filters</p>
            <p className="text-xs text-slate-500 mt-1">Events are classified as they arrive from intelligence feeds</p>
          </CardContent>
        </Card>
      ) : (
        <div data-testid="events-list" className="grid gap-3">
          {events.map((event) => (
            <EventCard key={event.event_id} event={event} onAction={handleAction} onDrillDown={handleDrillDown} />
          ))}
        </div>
      )}
    </div>
  );
}
